import {I18nKeys} from '@multipass-i18n';
import {Badge, SegmentedControl} from '@multipass-ui-components';
import {syncModelWithSelectorOptions, usePublishEvent, useSyncModelWithSelector} from 'esp-js-react';
import {ChevronLeft} from 'lucide-react';
import * as React from 'react';

import {LegalDocument} from '../../dal';
import {I18nKey, LEGAL_STORE_ID, LegalStoreModel, NavigateRequestedEvent, NAVIGATION_STORE_ID, NavigationEvents, useCurrentLocale, useT} from '../../logic';
import {formatEffectiveFrom, iconFor, toParagraphs} from './legalDocumentDisplay';

type Section = 'terms' | 'privacy';

const SECTIONS: Section[] = ['terms', 'privacy'];
const SECTION_LABEL_KEY: Record<Section, I18nKey> = {terms: I18nKeys.common.termsOfService, privacy: I18nKeys.common.privacyPolicy};

// The sticky header (~64px) + jump-nav must clear the scrolled-to heading, so anchor jumps offset by this.
const SCROLL_OFFSET = 120;

const linkClass = 'border-0 bg-transparent p-0 text-label font-semibold text-text-secondary no-underline hover:text-brand-emphasis';

export const LegalPage: React.FunctionComponent = () => {
    const legal = useSyncModelWithSelector<LegalStoreModel, LegalStoreModel>(
        m => m,
        syncModelWithSelectorOptions<LegalStoreModel>().setStoreId(LEGAL_STORE_ID)
    );
    const publishEvent = usePublishEvent();
    const t = useT();
    const currentLocale = useCurrentLocale();
    const [activeSection, setActiveSection] = React.useState<Section>('terms');

    const jumpTo = React.useCallback((section: string) => {
        const element = window.document.getElementById(section);
        if (element === null) {
            return;
        }
        window.scrollTo({top: element.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET, behavior: 'smooth'});
        window.history.replaceState(null, '', `#${section}`);
        setActiveSection(section as Section);
    }, []);

    const onBack = () => {
        if (window.history.length > 1) {
            window.history.back();
        } else {
            publishEvent(NAVIGATION_STORE_ID, NavigationEvents.navigateRequested, {path: '/'} satisfies NavigateRequestedEvent);
        }
    };

    React.useEffect(() => {
        if (!legal.loaded) {
            return;
        }
        const hash = window.location.hash.slice(1);
        if (hash === 'terms' || hash === 'privacy') {
            jumpTo(hash);
        }
        // IntersectionObserver is absent in some environments (e.g. jsdom under test); the active-section
        // highlight is a progressive enhancement, so skip it rather than throw.
        if (typeof IntersectionObserver === 'undefined') {
            return;
        }
        const observer = new IntersectionObserver(
            entries => entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setActiveSection(entry.target.id as Section);
                }
            }),
            {rootMargin: '-140px 0px -65% 0px', threshold: 0}
        );
        SECTIONS.forEach(section => {
            const element = window.document.getElementById(section);
            if (element !== null) {
                observer.observe(element);
            }
        });
        return () => observer.disconnect();
    }, [legal.loaded, jumpTo]);

    const documentFor = (section: Section): LegalDocument | undefined => legal.documents.find(document => document.type === section);

    return (
        <section className="px-5 pb-16 pt-10">
            <div className="mx-auto max-w-[47.5rem]">
                <button type="button" onClick={onBack} className={`mb-6 inline-flex items-center gap-1.5 ${linkClass}`}>
                    <ChevronLeft size={16} className="text-text-muted"/>
                    {t(I18nKeys.common.back)}
                </button>
                <h1 className="mb-2 text-headline">{t(I18nKeys.legal.title)}</h1>
                <p className="mb-6 max-w-[56ch] text-text-secondary">
                    {t(I18nKeys.legal.intro, {appName: t(I18nKeys.common.appName)})}
                </p>
                <div className="sticky top-[65px] z-[1] mb-2 bg-canvas py-3">
                    <SegmentedControl
                        ariaLabel={t(I18nKeys.legal.jumpNavAria)}
                        options={SECTIONS.map(section => ({value: section, label: t(SECTION_LABEL_KEY[section])}))}
                        value={activeSection}
                        onChange={jumpTo}
                    />
                </div>
                {legal.loaded ? (
                    SECTIONS.map((section, index) => {
                        const document = documentFor(section);
                        if (document === undefined) {
                            return null;
                        }
                        const Icon = iconFor(section);
                        return (
                            <section
                                key={section}
                                id={section}
                                className={`scroll-mt-32 pt-6 ${index > 0 ? 'mt-10 border-t border-border-subtle' : ''}`}
                            >
                                <div className="mb-2 flex items-center gap-3">
                                    <span className="grid h-11 w-11 flex-none place-items-center rounded-md bg-brand/15 text-brand-emphasis">
                                        <Icon size={22}/>
                                    </span>
                                    <h2 className="text-title">{t(SECTION_LABEL_KEY[section])}</h2>
                                </div>
                                <p className="mb-6 flex flex-wrap items-center gap-2 text-label text-text-muted">
                                    <Badge variant="neutral">{t(I18nKeys.common.version, {version: document.version})}</Badge>
                                    <span className="h-1 w-1 rounded-full bg-text-muted"/>
                                    {t(I18nKeys.legal.effective, {date: formatEffectiveFrom(document.effectiveFrom, currentLocale)})}
                                </p>
                                <div className="text-body leading-relaxed text-text-primary">
                                    {toParagraphs(document.body).map((paragraph, paragraphIndex) => (
                                        <p key={paragraphIndex} className="mb-5 last:mb-0">{paragraph}</p>
                                    ))}
                                </div>
                            </section>
                        );
                    })
                ) : (
                    <p className="text-body text-text-secondary">{t(I18nKeys.legal.loading)}</p>
                )}
                <footer className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-border-subtle pt-8">
                    <span className="text-caption text-text-muted">{t(I18nKeys.common.footerCopyright, {appName: t(I18nKeys.common.appName)})}</span>
                    <nav aria-label={t(I18nKeys.legal.title)} className="flex gap-5">
                        <button type="button" onClick={() => jumpTo('terms')} className={linkClass}>{t(I18nKeys.common.termsOfService)}</button>
                        <button type="button" onClick={() => jumpTo('privacy')} className={linkClass}>{t(I18nKeys.common.privacyPolicy)}</button>
                    </nav>
                </footer>
            </div>
        </section>
    );
};
